import PropTypes from "prop-types";
import BaseNode from "./BaseNode";
import { RealisticTank } from "./realistic";

/**
 * TankNode – Industrial tank visualization.
 * Supports:
 * - Realistic 3D SCADA design with dynamic sight glass liquid level
 * - Dynamic fill level from LIVE device value
 * - Premium glassmorphism design switch
 * - Classic industrial SVG layout
 */
const TankNode = (props) => {
  const { data } = props;
  const minValue = data?.config?.minValue ?? 0;
  const maxValue = data?.config?.maxValue ?? 100;
  const warningThreshold = data?.config?.warningThreshold ?? 80;
  const criticalThreshold = data?.config?.criticalThreshold ?? 95;

  return (
    <BaseNode {...props}>
      {({ liveValue, isRunning, isOffline, statusColor, designType, color, opacity, unit, config, width, height }) => {
        if (designType === "realistic") {
          return (
            <RealisticTank
              liveValue={liveValue}
              unit={unit || "%"}
              isRunning={isRunning}
              isOffline={isOffline}
              statusColor={statusColor}
              config={config || data?.config}
              opacity={opacity}
              width={width}
              height={height}
            />
          );
        }
        // Dynamic percentage: use live data if available, fallback to config
        let fillLevel;
        if (liveValue !== null && liveValue !== undefined) {
          const range = maxValue - minValue;
          fillLevel = range > 0 ? ((liveValue - minValue) / range) * 100 : 0;
        } else {
          fillLevel = data?.config?.fillLevel ?? 0;
        }

        // Clamp 0-100
        fillLevel = Math.min(100, Math.max(0, fillLevel));

        const isWarning = fillLevel >= warningThreshold;
        const isCritical = fillLevel >= criticalThreshold;

        // Color states
        const fillColor = isCritical ? "#dc2626" : isWarning ? "#f59e0b" : color;

        // Glass dimension constants
        const glassHeight = 120;
        const glassTop = 70;
        const fillHeight = (fillLevel / 100) * glassHeight;
        const fillY = glassTop + glassHeight - fillHeight;

        const isNewDesign = designType === "new_design";

        return (
          <svg
            width="140"
            height="248"
            viewBox="0 0 140 248"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              opacity: props.data?.styles?.opacity ?? 1,
              filter: isOffline ? "grayscale(100%) opacity(0.6)" : "none",
              overflow: "visible",
              transition: "all 0.3s ease",
            }}
          >
            <defs>
              <linearGradient id={`tankGrad-${props.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#94a3b8" />
                <stop offset="15%" stopColor="#cbd5e1" />
                <stop offset="40%" stopColor="#f8fafc" />
                <stop offset="85%" stopColor="#94a3b8" />
                <stop offset="100%" stopColor="#64748b" />
              </linearGradient>
              <linearGradient id={`tankGlassGrad-${props.id}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#ffffff" stopOpacity="0.25" />
                <stop offset="100%" stopColor="#ffffff" stopOpacity="0.05" />
              </linearGradient>
              <linearGradient id={`liquidGrad-${props.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={fillColor} stopOpacity="1" />
                <stop offset="100%" stopColor={fillColor} stopOpacity="0.7" />
              </linearGradient>
              <filter id="liquidGlow" x="-20%" y="-20%" width="140%" height="140%">
                <feGaussianBlur stdDeviation="3" result="blur" />
                <feComposite in="SourceGraphic" in2="blur" operator="over" />
              </filter>
            </defs>

            {isNewDesign ? (
              // ─── NEW PREMIUM GLASSMORPHIC DESIGN ───
              <>
                {/* Legs */}
                <path d="M 40 210 L 32 232" stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />
                <path d="M 100 210 L 108 232" stroke="#1e293b" strokeWidth="6" strokeLinecap="round" />

                {/* Main Body */}
                <rect x="20" y="30" width="100" height="180" fill="url(#conveyerGlassGrad)" stroke="#64748b" strokeWidth="1.5" rx="12" />

                {/* Glass Inner backing plate */}
                <rect x="35" y="65" width="70" height="130" fill="url(#conveyerGlassGrad)" stroke="#475569" strokeWidth="1.5" rx="8" />

                {/* Glass Cutout Background (Empty part) */}
                <rect x="42" y="70" width="22" height="120" fill="#1e293b" rx="4" />

                {/* Liquid Fill with Glow */}
                {fillHeight > 0 && (
                  <rect
                    x="42"
                    y={fillY}
                    width="22"
                    height={fillHeight}
                    fill={`url(#liquidGrad-${props.id})`}
                    rx="4"
                    style={{
                      filter: "url(#liquidGlow)",
                      transition: "all 0.5s ease-out",
                    }}
                  />
                )}

                {/* Glass Outline */}
                <rect x="42" y="70" width="22" height="120" fill="none" stroke="#475569" strokeWidth="2.5" rx="4" />

                {/* Scale Marks */}
                {[...Array(11)].map((_, i) => {
                  const y = 70 + i * 12;
                  const val = maxValue - ((maxValue - minValue) * (i / 10));
                  return (
                    <g key={i}>
                      <line x1="68" y1={y} x2="74" y2={y} stroke="#334155" strokeWidth="1.5" />
                      <text x="78" y={y + 3} fontSize="9" fontWeight="bold" fill="#000000" fontFamily="sans-serif">
                        {Math.round(val)}
                      </text>
                    </g>
                  );
                })}

                {/* Warning Indicator Dot */}
                {(isWarning || isCritical) && (
                  <circle
                    cx="115"
                    cy="20"
                    r="8"
                    fill={isCritical ? "#dc2626" : "#f59e0b"}
                    stroke="#ffffff"
                    strokeWidth="2"
                    style={{ filter: "drop-shadow(0px 0px 6px rgba(0,0,0,0.5))" }}
                  />
                )}
              </>
            ) : (
              // ─── CLASSIC INDUSTRIAL SVG DESIGN ───
              <>
                {/* Legs */}
                <path d="M 40 210 L 30 225" stroke="#3e2723" strokeWidth="6" strokeLinecap="round" />
                <path d="M 100 210 L 110 225" stroke="#3e2723" strokeWidth="6" strokeLinecap="round" />

                {/* Main Body */}
                <rect x="20" y="30" width="100" height="180" fill={`url(#tankGrad-${props.id})`} />

                {/* Top Cap */}
                <ellipse cx="70" cy="30" rx="50" ry="15" fill={`url(#tankGrad-${props.id})`} />
                <ellipse cx="70" cy="27" rx="46" ry="12" fill="none" stroke="#f8fafc" strokeWidth="1.5" opacity="0.6" />
                <ellipse cx="70" cy="30" rx="50" ry="15" fill="none" stroke="#64748b" strokeWidth="1.5" />

                {/* Bottom Cap */}
                <ellipse cx="70" cy="210" rx="50" ry="15" fill={`url(#tankGrad-${props.id})`} />
                <ellipse cx="70" cy="210" rx="50" ry="15" fill="none" stroke="#64748b" strokeWidth="1.5" />

                {/* Ribs */}
                <line x1="20" y1="45" x2="120" y2="45" stroke="#64748b" strokeWidth="1.5" />
                <line x1="20" y1="47" x2="120" y2="47" stroke="#f8fafc" strokeWidth="1.5" opacity="0.5" />
                <line x1="20" y1="60" x2="120" y2="60" stroke="#64748b" strokeWidth="1.5" />
                <line x1="20" y1="62" x2="120" y2="62" stroke="#f8fafc" strokeWidth="1.5" opacity="0.5" />

                {/* Backing Plate */}
                <rect x="35" y="65" width="70" height="130" fill="#e2e8f0" stroke="#94a3b8" strokeWidth="1.5" rx="2" />

                {/* Glass Cutout Background */}
                <rect x="42" y="70" width="22" height="120" fill="#cbd5e1" />

                {/* Liquid Fill */}
                {fillHeight > 0 && (
                  <rect
                    x="42"
                    y={fillY}
                    width="22"
                    height={fillHeight}
                    fill={fillColor}
                  />
                )}

                {/* Glass Outline */}
                <rect x="42" y="70" width="22" height="120" fill="none" stroke="#0f172a" strokeWidth="2.5" />

                {/* Scale Marks */}
                {[...Array(11)].map((_, i) => {
                  const y = 70 + i * 12;
                  const val = maxValue - ((maxValue - minValue) * (i / 10));
                  return (
                    <g key={i}>
                      <line x1="68" y1={y} x2="75" y2={y} stroke="#0f172a" strokeWidth="2" />
                      <text x="80" y={y + 4} fontSize="10" fontWeight="bold" fill="#0f172a" fontFamily="sans-serif">
                        {Math.round(val)}
                      </text>
                    </g>
                  );
                })}

                {/* Warning Indicator */}
                {(isWarning || isCritical) && (
                  <circle cx="115" cy="20" r="8" fill={isCritical ? "#dc2626" : "#f59e0b"} stroke="#fff" strokeWidth="2" />
                )}
              </>
            )}

            {/* Status text - clearly below tank legs */}
            <text x="70" y="244" textAnchor="middle" fontSize="8" fontWeight="800" fill={statusColor === "grey" ? "#94a3b8" : "#0f172a"} style={{ letterSpacing: 0.5 }}>
              {isOffline ? "OFFLINE" : "ONLINE"}
            </text>
          </svg>
        );
      }}
    </BaseNode>
  );
};

TankNode.propTypes = {
  data: PropTypes.object,
  selected: PropTypes.bool,
  id: PropTypes.string,
};

export default TankNode;
